import { Link } from "react-router-dom";
import type { Receipt } from "@/api/types";
import { CurrencyBadge } from "./CurrencyBadge";
import { ReceiptStatusBadge } from "./ReceiptStatusBadge";
import { Button } from "./Button";
import { attentionReason, attentionReasonLabel } from "@/utils/receiptReadiness";
import { receiptTitle } from "@/utils/receipt";
import "./receipt-card.css";
import "./attention-receipt-row.css";

export interface AttentionReceiptRowProps {
  tripSlug: string;
  receipt: Receipt;
  paidByLabel?: string;
  /** Retry parsing — only offered for the R-FAILED (parse_failed) bucket. */
  onRetry?: () => void;
  retrying?: boolean;
  /** Delete — the universal escape hatch (ADR-0029, AC-03.8). */
  onDelete: () => void;
  deleting?: boolean;
}

/**
 * A row in the filtered "потребують уваги" view (receipts-list.md). It reuses
 * the existing <ReceiptStatusBadge> per-row labelling (AC-19.5 stands — no new
 * per-receipt marker) and the existing soft inline hint as the reason. Each
 * row offers its resolution path: tap opens receipt-detail to finish (set
 * payer / set currency); failed rows also offer [Повторити]; all rows offer
 * [Видалити] so the gate is never a dead-end.
 */
export function AttentionReceiptRow({
  tripSlug,
  receipt,
  paidByLabel,
  onRetry,
  retrying,
  onDelete,
  deleting,
}: AttentionReceiptRowProps) {
  const reason = attentionReason(receipt);
  const title = receiptTitle(receipt);

  return (
    <li className="wp-rcpt" data-testid="attention-row" data-receipt-id={receipt.receipt_id}>
      <Link
        to={`/t/${tripSlug}/receipts/${receipt.receipt_id}`}
        className="wp-rcpt__link"
        data-testid="receipt-card"
      >
        <div className="wp-rcpt__row">
          <ReceiptStatusBadge status={receipt.status} />
          <span className="wp-rcpt__title">{title}</span>
          <CurrencyBadge ccy={receipt.source_ccy} />
        </div>
        {paidByLabel ? (
          <div className="wp-rcpt__paid-by">{paidByLabel}</div>
        ) : null}
        {reason ? (
          <div className="wp-attn__reason">
            <span aria-hidden="true">⚠</span> {attentionReasonLabel(reason)}
          </div>
        ) : null}
      </Link>
      <div className="wp-attn__actions">
        {onRetry ? (
          <Button size="sm" variant="secondary" onClick={onRetry} loading={retrying}>
            Повторити
          </Button>
        ) : null}
        <Button size="sm" variant="danger" onClick={onDelete} loading={deleting}>
          Видалити
        </Button>
      </div>
    </li>
  );
}
